import { cloneElement, FC, isValidElement, ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import LocalOfferRounded from '@mui/icons-material/LocalOfferRounded'
import SportsEsportsRounded from '@mui/icons-material/SportsEsportsRounded'
import VideoLibraryRounded from '@mui/icons-material/VideoLibraryRounded'
import { Box, Card, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { optionalClickableProps } from '../../helpers/interactive'
import { UAInsightsSummaryStats } from '../../types'

/** Palette family that gives each summary card its colour identity. */
type SummaryColor = 'primary' | 'secondary' | 'info'

interface SummaryCardProps {
  icon: ReactElement
  label: string
  value: string | number
  color?: SummaryColor
  onClick?: () => void
}

/**
 * KPI tile for the dashboard summary strip. White card with a small diagonal
 * colour gradient in the top-left corner and a tinted icon badge that lifts in
 * sync with the card on hover, mirroring the game Overview KPI cards.
 */
const SummaryCard: FC<SummaryCardProps> = ({ icon, label, value, color = 'primary', onClick }) => {
  const badgeIcon = isValidElement(icon) ? cloneElement(icon, { sx: { fontSize: 26, color: 'inherit' } } as Record<string, unknown>) : null

  return (
    <Card
      variant="outlined"
      {...optionalClickableProps(onClick)}
      sx={(theme) => {
        const main = theme.palette[color].main
        return {
          flex: 1,
          minWidth: 0,
          position: 'relative',
          overflow: 'hidden',
          px: 2.25,
          pt: 2,
          pb: 1.75,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          cursor: onClick ? 'pointer' : 'default',
          // White base with a small diagonal colour gradient in the top-left
          // corner so each KPI keeps a hint of its identity.
          backgroundColor: 'background.paper',
          backgroundImage: `linear-gradient(135deg, ${alpha(main, 0.07)} 0%, ${alpha(main, 0)} 40%)`,
          transition: 'box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease',
          '& .kpi-badge': { transition: 'transform 0.2s ease, background-color 0.2s ease' },
          '&:hover': onClick
            ? {
                boxShadow: 6,
                transform: 'translateY(-2px)',
                borderColor: alpha(main, 0.55),
                '& .kpi-badge': { transform: 'scale(1.08)', bgcolor: alpha(main, 0.2) },
              }
            : { boxShadow: 3 },
          '&:focus-visible': onClick ? { outline: '2px solid', outlineColor: main, outlineOffset: 2 } : undefined,
        }
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.7rem', lineHeight: 1.3 }}
      >
        {label}
      </Typography>
      <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          className="kpi-badge"
          sx={(theme) => ({
            flexShrink: 0,
            width: 46,
            height: 46,
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette[color].main,
            bgcolor: alpha(theme.palette[color].main, 0.12),
          })}
        >
          {badgeIcon}
        </Box>
        <Typography
          sx={{ flex: 1, minWidth: 0, fontWeight: 'bold', color: `${color}.main`, lineHeight: 1, fontSize: '2.25rem', fontVariantNumeric: 'tabular-nums' }}
        >
          {value}
        </Typography>
      </Box>
    </Card>
  )
}

interface UAInsightsSummaryCardsProps {
  stats: UAInsightsSummaryStats
  onTotalCreativesClick?: () => void
  onGamesTrackedClick?: () => void
  onUniqueTagsClick?: () => void
}

export const UAInsightsSummaryCards: FC<UAInsightsSummaryCardsProps> = ({ stats, onTotalCreativesClick, onGamesTrackedClick, onUniqueTagsClick }) => {
  const { t } = useTranslation('ua-insights')

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <SummaryCard
        icon={<VideoLibraryRounded />}
        label={t('summary_total_creatives')}
        value={stats.creativesCount.toLocaleString()}
        onClick={onTotalCreativesClick}
      />
      <SummaryCard
        icon={<SportsEsportsRounded />}
        label={t('summary_games_tracked')}
        value={stats.gamesCount.toLocaleString()}
        color="secondary"
        onClick={onGamesTrackedClick}
      />
      <SummaryCard
        icon={<LocalOfferRounded />}
        label={t('summary_unique_tags')}
        value={stats.tagsCount.toLocaleString()}
        color="info"
        onClick={onUniqueTagsClick}
      />
    </Box>
  )
}
